import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { WorkflowState, EditWorkflows, Workflows } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import ScreenUrl from '../../../router/url';
import router from '../../../router';

export const actions: ActionTree<WorkflowState, RootState> = {
    /**
     * ワークフロー一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Workflow.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ワークフロー新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままのエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async clearWorkflowError({ commit }) {
        commit(types.CLEAR_WORKFLOW_ERROR);
    },

    /**
     * ワークフロー新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {} newWorkflow 新規ワークフロー情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newWorkflow: Workflows) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.Workflow.CREATE, params: newWorkflow },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Workflow.CREATED);
                dispatch('toastMessage', `${newWorkflow.workflow.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ワークフローの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ワークフローID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Workflow.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ワークフロー更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditWorkflows} editWorkflow 更新するワークフロー情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editWorkflow: EditWorkflows) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.Workflow.UPDATE.replace(':id', editWorkflow.workflow.id.toString()),
                    params: editWorkflow,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 一覧画面へ戻る
                router.push(ScreenUrl.Workflow.INDEX);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Workflow.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ワークフロー削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ワークフローID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.Workflow.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 一覧画面へ戻る
                router.push(ScreenUrl.Workflow.INDEX);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Workflow.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { sort } = {}) {
        const payload = {
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.Workflow.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ワークフローの詳細のリセット
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async detailReset({ commit }) {
        commit(types.DETAIL_RESET);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
